import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import type { PucCuenta } from "@/api/empresas";

interface PucFormModalProps {
  initial: PucCuenta | null;
  onCancel: () => void;
  onSubmit: (body: {
    codigo_puc: string;
    nombre_cuenta: string;
    porcentaje_retencion?: number;
  }) => void;
  loading: boolean;
}

export const PucFormModal = ({
  initial,
  onCancel,
  onSubmit,
  loading,
}: PucFormModalProps) => {
  const [codigo, setCodigo] = useState(initial?.codigo_puc ?? "");
  const [nombre, setNombre] = useState(initial?.nombre_cuenta ?? "");
  const [porcentaje, setPorcentaje] = useState(
    initial?.porcentaje_retencion?.toString() ?? "",
  );
  const dialogRef = useFocusTrap(true, { onEscape: onCancel });

  const submit = () => {
    onSubmit({
      codigo_puc: codigo.trim(),
      nombre_cuenta: nombre.trim(),
      porcentaje_retencion: porcentaje ? Number(porcentaje) : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="puc-form-title"
      onClick={onCancel}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="puc-form-title"
          className="text-base font-semibold text-ink-primary"
        >
          {initial ? "Editar" : "Nueva"} cuenta PUC
        </h2>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-secondary">
              Código PUC <span className="text-accent-red">*</span>
            </label>
            <input
              className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              required
              placeholder="110505"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-secondary">
              Nombre de la cuenta <span className="text-accent-red">*</span>
            </label>
            <textarea
              className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue min-h-[80px]"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              required
              rows={2}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-secondary">
              % Retención (opcional)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:border-accent-blue focus:outline-none focus:ring-1 focus:ring-accent-blue"
              value={porcentaje}
              onChange={(e) => setPorcentaje(e.target.value)}
              placeholder="ej: 2.5"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={loading}>
            Guardar
          </Button>
        </div>
      </div>
    </div>
  );
};